/* ============================================================
   i18n.jsx — bilingual (KO / EN) string layer
   - UI[lang][key] holds chrome strings (supports {var} interpolation)
   - LangContext carries the current language; useT() returns { lang, t }
   - content helpers: tagLabel(), and arrays for prompt descriptors
   ============================================================ */

const LangContext = React.createContext("ko");

const UI = {
  ko: {
    /* nav / header */
    nav_generate: "생성하기", nav_mypage: "내 작업",
    search_ph: "프롬프트·태그·작가 검색",
    btn_create: "만들기", login: "로그인",
    menu_mywork: "내 작업", menu_saved: "저장한 이미지", menu_settings: "설정", logout: "로그아웃",
    menu: "메뉴", account: "계정", close: "닫기",

    /* login modal */
    lm_title: "로그인 / 회원가입", lm_sub: "Promzi와 함께 상상을 작품으로",
    lm_email: "이메일 주소", lm_pw: "비밀번호", lm_login: "로그인", lm_register: "가입하기",
    lm_login_tab: "로그인", lm_register_tab: "회원가입", lm_or: "또는",
    lm_continue_email: "이메일로 계속하기", lm_continue_kakao: "카카오 계정으로 계속하기",
    lm_login_failed: "로그인에 실패했어요", lm_register_failed: "회원가입에 실패했어요",
    lm_terms: "계속 진행하면 이용약관과 개인정보처리방침에 동의하는 것으로 간주됩니다.",

    /* toasts */
    t_welcome: "환영합니다, {name}님", t_loggedout: "로그아웃되었어요",
    t_downloaded_hi: "고해상도 이미지를 내려받았어요", t_link_copied: "링크를 복사했어요",
    t_downloaded: "이미지를 내려받았어요",

    /* gallery */
    g_eyebrow: "Explore", g_title: "갤러리 탐색",
    g_sub: "크리에이터들이 만든 {n}개의 작품 · 클릭하면 프롬프트를 볼 수 있어요",
    g_cta: "Promzi로 만들기", g_upload: "업로드", tag_all: "전체",
    tag_more: "더보기", tag_collapse: "접기",
    sort_pop: "인기순", sort_new: "최신순", sort_views: "조회순",
    g_search_result: "\"{q}\" 검색결과 {n}개", reset: "초기화",
    loading_more: "더 불러오는 중", all_loaded: "모든 작품을 불러왔어요",
    empty_title: "결과가 없어요", empty_desc: "다른 키워드나 태그로 검색해 보세요.",
    search_hint: "프롬프트, 태그, 작가명으로 바로 찾을 수 있음.",
    up_title: "작품 업로드",
    up_sub: "갤러리에 올릴 이미지를 준비하고 프롬프트와 태그를 함께 정리해보세요.",
    up_name: "업로더",
    up_image: "이미지",
    up_pick: "이미지 선택",
    up_change: "다른 이미지 선택",
    up_image_hint: "JPG, PNG, WEBP 파일 업로드 가능",
    up_prompt: "프롬프트",
    up_prompt_ph: "이 이미지에 사용한 프롬프트를 적어주세요",
    up_tags: "태그",
    up_tags_ph: "예) 네온, 인물, 시네마틱",
    up_tags_hint: "쉼표(,)로 여러 태그를 입력할 수 있음",
    up_preview: "미리보기",
    up_submit: "업로드하기",
    up_submit_idle: "이미지, 프롬프트, 태그를 입력하면 업로드할 수 있어요",
    up_recent: "이번 세션 업로드",
    up_recent_empty: "아직 업로드한 작품이 없어요",
    up_success: "업로드 초안이 저장됐어요",
    up_gate_title: "로그인하고 업로드하세요",
    up_gate_desc: "업로드 페이지는 로그인한 사용자만 사용할 수 있어요.<br>로그인 후 작품과 프롬프트를 함께 올려보세요.",
    up_gate_btn: "로그인하고 업로드하기",

    /* detail */
    back_gallery: "갤러리", follow: "팔로우",
    gen_from_prompt: "이 프롬프트로 생성", download: "다운로드",
    like: "좋아요", save: "저장", share: "공유",
    stat_likes: "좋아요", stat_views: "조회", stat_saves: "저장",
    prompt: "프롬프트", copy: "복사", copied: "복사됨",
    gen_info: "생성 정보",
    m_ar: "비율", m_size: "크기", m_resolution: "용량",
    variations: "변형 만들기", new_variation: "새 변형",
    variation_hint: "썸네일을 누르면 같은 프롬프트의 변형을 자세히 볼 수 있어요.",
    similar_works: "비슷한 작품", days_ago: "{n}일 전",

    /* generate */
    f_style: "스타일", f_ar: "비율", f_model: "모델", f_count: "생성 개수 · {n}장",
    count_unit: "{n}장",
    gen_title: "이미지 생성", gen_run: "생성하기", credits: "· 크레딧 {n}",
    prompt_ph: "예) 비 내리는 네온 사이버펑크 골목, 젖은 아스팔트의 반사광, 시네마틱 조명",
    studio_empty_title: "여기에 결과가 나타나요",
    studio_empty_a: "왼쪽에서 프롬프트를 입력하고 옵션을 고른 뒤 ", studio_empty_b: "를 눌러보세요.",
    gen_loading: "이미지를 생성하고 있어요…",
    done_badge: "완료", result_meta: "{n}장 · {model}", regenerate: "다시 생성",
    results_hint: "마음에 드는 결과를 클릭해 변형하거나, 프롬프트를 다듬어 다시 생성해 보세요.",
    focus_badge: "집중 모드", focus_title: "무엇을 만들어 볼까요?",
    opt_expand: "옵션 펼치기", opt_collapse: "옵션 접기", try_these: "이렇게 시작해 보세요",
    chat_title: "대화하듯 이미지를 만들어요", chat_sub: "아래에 설명을 입력하고 보내면, 결과가 차곡차곡 쌓여요.",
    chat_ph: "만들고 싶은 이미지를 설명하세요…",
    gate_title: "로그인하고 시작하세요",
    gate_desc: "이미지 생성은 로그인한 회원만 이용할 수 있어요.<br>가입하면 매일 무료 크레딧을 드립니다.",
    gate_btn: "로그인하고 생성하기", gate_browse: "갤러리 먼저 둘러보기",

    /* mypage */
    mp_login_title: "로그인이 필요해요", mp_login_desc: "내 작업과 저장한 이미지를 보려면 로그인하세요.",
    tab_created: "생성한 이미지", tab_saved: "저장함", tab_liked: "좋아요",
    role_creator: "크리에이터", mp_new: "새로 만들기",
    st_works: "작품", st_likes_recv: "좋아요받음", st_followers: "팔로워", st_following: "팔로잉",
    credit_badge: "크레딧 {n} / 100",
    mp_empty_liked_title: "아직 좋아요한 이미지가 없어요", mp_empty_liked_desc: "갤러리에서 마음에 드는 작품에 하트를 눌러보세요.",
    mp_empty_title: "아직 비어 있어요", mp_empty_desc: "첫 작품을 만들어 채워보세요.",
    mp_view_gallery: "갤러리 보기", mp_make_image: "이미지 만들기",
    settings: "설정",
    settings_login_title: "설정은 로그인 후 이용할 수 있어요", settings_login_desc: "계정과 표시 환경을 관리하려면 로그인하세요.",
    settings_profile: "프로필", settings_display_name: "표시 이름", settings_handle: "핸들", settings_email: "이메일",
    settings_api_keys: "API 키",
    api_keys_desc: "API로 이미지를 업로드할 때 쓰는 키예요. 최대 5개까지 만들 수 있어요.",
    api_keys_new: "새 키 만들기", api_keys_name_ph: "키 이름 (예: CLI 업로드)",
    api_keys_create: "생성", api_keys_cancel: "취소",
    api_keys_created_notice: "이 키는 지금 한 번만 표시돼요. 안전한 곳에 복사해 두세요.",
    api_keys_copy: "복사", api_keys_copied: "키를 복사했어요", api_keys_done: "확인",
    api_keys_revoke: "폐기", api_keys_revoke_confirm: "이 키를 폐기할까요? 폐기하면 되돌릴 수 없어요.",
    api_keys_empty: "아직 만든 키가 없어요.",
    api_keys_created_at: "생성", api_keys_last_used: "마지막 사용", api_keys_never_used: "사용 안 함",
    api_keys_error: "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.",
    admin_delete: "관리자 삭제",
    admin_delete_confirm: "이 이미지를 삭제할까요? 관리자 권한으로 다른 사용자의 작품을 삭제합니다.",
    t_admin_deleted: "이미지를 삭제했어요",
    admin_delete_failed: "삭제에 실패했어요. 잠시 후 다시 시도해 주세요.",
    settings_preferences: "환경 설정", settings_language: "언어", settings_theme: "테마",
    theme_system: "시스템", theme_light: "라이트", theme_dark: "다크",

    /* footer */
    foot_terms: "이용약관", foot_privacy: "개인정보처리방침",
  },

  en: {
    nav_generate: "Generate", nav_mypage: "My Work",
    search_ph: "Search prompts, tags, creators",
    btn_create: "Create", login: "Log in",
    menu_mywork: "My Work", menu_saved: "Saved", menu_settings: "Settings", logout: "Log out",
    menu: "Menu", account: "Account", close: "Close",

    lm_title: "Log in / Sign up", lm_sub: "Turn imagination into art with Promzi",
    lm_email: "Email address", lm_pw: "Password", lm_login: "Log in", lm_register: "Sign up",
    lm_login_tab: "Log in", lm_register_tab: "Sign up", lm_or: "or",
    lm_continue_email: "Continue with email", lm_continue_kakao: "Continue with Kakao",
    lm_login_failed: "Login failed", lm_register_failed: "Sign up failed",
    lm_terms: "By continuing you agree to the Terms of Service and Privacy Policy.",

    t_welcome: "Welcome, {name}", t_loggedout: "You've been logged out",
    t_downloaded_hi: "Hi-res image downloaded", t_link_copied: "Link copied",
    t_downloaded: "Image downloaded",

    g_eyebrow: "Explore", g_title: "Explore the gallery",
    g_sub: "{n} works by creators · tap any to see its prompt",
    g_cta: "Create with Promzi", g_upload: "Upload", tag_all: "All",
    tag_more: "More", tag_collapse: "Collapse",
    sort_pop: "Popular", sort_new: "Newest", sort_views: "Most viewed",
    g_search_result: "{n} results for \"{q}\"", reset: "Reset",
    loading_more: "Loading more", all_loaded: "You've reached the end",
    empty_title: "No results", empty_desc: "Try a different keyword or tag.",
    search_hint: "Search instantly by prompt, tag, or creator.",
    up_title: "Upload a work",
    up_sub: "Prepare an image for the gallery and pair it with its prompt and tags.",
    up_name: "Uploader",
    up_image: "Image",
    up_pick: "Choose image",
    up_change: "Choose another image",
    up_image_hint: "JPG, PNG, and WEBP files are supported",
    up_prompt: "Prompt",
    up_prompt_ph: "Write the prompt used for this image",
    up_tags: "Tags",
    up_tags_ph: "e.g. neon, portrait, cinematic",
    up_tags_hint: "Use commas to add multiple tags",
    up_preview: "Preview",
    up_submit: "Upload",
    up_submit_idle: "Add an image, prompt, and tags to upload",
    up_recent: "Uploads in this session",
    up_recent_empty: "No uploads yet",
    up_success: "Upload draft saved",
    up_gate_title: "Log in to upload",
    up_gate_desc: "The upload page is available to signed-in users only.<br>Log in to upload works with prompts and tags.",
    up_gate_btn: "Log in & upload",

    back_gallery: "Gallery", follow: "Follow",
    gen_from_prompt: "Use this prompt", download: "Download",
    like: "Like", save: "Save", share: "Share",
    stat_likes: "Likes", stat_views: "Views", stat_saves: "Saves",
    prompt: "Prompt", copy: "Copy", copied: "Copied",
    gen_info: "Generation info",
    m_ar: "Ratio", m_size: "Size", m_resolution: "File size",
    variations: "Variations", new_variation: "New set",
    variation_hint: "Tap a thumbnail to open a variation of the same prompt.",
    similar_works: "Similar works", days_ago: "{n}d ago",

    f_style: "Style", f_ar: "Ratio", f_model: "Model", f_count: "Images · {n}",
    count_unit: "{n}",
    gen_title: "Generate", gen_run: "Generate", credits: "· {n} credits",
    prompt_ph: "e.g. Neon cyberpunk alley in the rain, reflections on wet asphalt, cinematic lighting",
    studio_empty_title: "Your results appear here",
    studio_empty_a: "Enter a prompt on the left, pick your options, then hit ", studio_empty_b: ".",
    gen_loading: "Generating images…",
    done_badge: "Done", result_meta: "{n} images · {model}", regenerate: "Regenerate",
    results_hint: "Click a result you like to remix it, or refine the prompt and generate again.",
    focus_badge: "Focus mode", focus_title: "What shall we create?",
    opt_expand: "Show options", opt_collapse: "Hide options", try_these: "Try one of these",
    chat_title: "Create images like a conversation", chat_sub: "Type a description below and send — results stack up here.",
    chat_ph: "Describe the image you want to make…",
    gate_title: "Log in to get started",
    gate_desc: "Image generation is available to members only.<br>Sign up for free daily credits.",
    gate_btn: "Log in & generate", gate_browse: "Browse the gallery first",

    mp_login_title: "Log in required", mp_login_desc: "Log in to see your work and saved images.",
    tab_created: "Created", tab_saved: "Saved", tab_liked: "Liked",
    role_creator: "Creator", mp_new: "Create new",
    st_works: "Works", st_likes_recv: "Likes received", st_followers: "Followers", st_following: "Following",
    credit_badge: "{n} / 100 credits",
    mp_empty_liked_title: "No liked images yet", mp_empty_liked_desc: "Tap the heart on works you love in the gallery.",
    mp_empty_title: "Nothing here yet", mp_empty_desc: "Make your first work to fill this up.",
    mp_view_gallery: "View gallery", mp_make_image: "Create an image",
    settings: "Settings",
    settings_login_title: "Log in to open settings", settings_login_desc: "Log in to manage your account and display preferences.",
    settings_profile: "Profile", settings_display_name: "Display name", settings_handle: "Handle", settings_email: "Email",
    settings_api_keys: "API keys",
    api_keys_desc: "Keys for uploading images through the API. You can create up to 5.",
    api_keys_new: "Create new key", api_keys_name_ph: "Key name (e.g. CLI upload)",
    api_keys_create: "Create", api_keys_cancel: "Cancel",
    api_keys_created_notice: "This key is shown only once. Copy it somewhere safe.",
    api_keys_copy: "Copy", api_keys_copied: "Key copied", api_keys_done: "Done",
    api_keys_revoke: "Revoke", api_keys_revoke_confirm: "Revoke this key? This cannot be undone.",
    api_keys_empty: "No keys yet.",
    api_keys_created_at: "Created", api_keys_last_used: "Last used", api_keys_never_used: "Never",
    api_keys_error: "Something went wrong. Please try again.",
    admin_delete: "Admin delete",
    admin_delete_confirm: "Delete this image? You are removing another user's work with admin privileges.",
    t_admin_deleted: "Image deleted",
    admin_delete_failed: "Delete failed. Please try again.",
    settings_preferences: "Preferences", settings_language: "Language", settings_theme: "Theme",
    theme_system: "System", theme_light: "Light", theme_dark: "Dark",

    foot_terms: "Terms", foot_privacy: "Privacy",
  },
};

function tr(lang, key, vars) {
  let s = (UI[lang] && UI[lang][key] != null) ? UI[lang][key] : (UI.ko[key] != null ? UI.ko[key] : key);
  if (vars) for (const k in vars) s = s.split("{" + k + "}").join(String(vars[k]));
  return s;
}

function useT() {
  const lang = React.useContext(LangContext);
  const t = React.useCallback((k, v) => tr(lang, k, v), [lang]);
  return { lang, t };
}

/* ---- content helpers ---- */
const TAG_EN = {
  "사이버펑크": "Cyberpunk", "네온": "Neon", "도시": "City", "추상": "Abstract", "미니멀": "Minimal",
  "파스텔": "Pastel", "풍경": "Landscape", "시네마틱": "Cinematic", "3D": "3D", "프로덕트": "Product",
  "일러스트": "Illustration", "수채화": "Watercolor", "판타지": "Fantasy", "자연": "Nature",
  "레트로": "Retro", "신스웨이브": "Synthwave", "겨울": "Winter", "오브제": "Object", "초현실": "Surreal",
  "SF": "Sci-Fi", "캐릭터": "Character", "인물": "Portrait", "더블노출": "Double Exposure",
  "타이포": "Typography", "봄": "Spring", "우주": "Space", "포스터": "Poster", "리소": "Riso",
  "매크로": "Macro", "밤": "Night", "동양화": "Ink Art", "빈티지": "Vintage",
};
function tagLabel(tag, lang) { return lang === "en" ? (TAG_EN[tag] || tag) : tag; }

/* descriptor pools used by buildFullPrompt() */
const PROMPT_DESC = {
  ko: {
    lighting: ["시네마틱 조명", "황금빛 역광", "부드러운 자연광", "드라마틱한 명암 대비", "은은한 림 라이트", "푸른 시간대의 빛"],
    camera: ["85mm 망원 렌즈", "얕은 피사계 심도", "와이드 앵글 구도", "매크로 클로즈업", "로우 앵글 시점"],
    detail: ["정교한 디테일", "섬세한 질감 묘사", "사실적인 재질감", "풍부한 색 계조", "선명한 윤곽선"],
    mood: ["몽환적인 분위기", "고요하고 정적인 무드", "생동감 넘치는 색감", "차분한 톤", "초현실적인 공기감"],
    quality: ["초고해상도 8K", "프로페셔널 포토그래피", "아트스테이션 트렌딩", "고품질 렌더링", "디테일한 마감"],
    join: ", ", end: ".",
  },
  en: {
    lighting: ["cinematic lighting", "golden backlight", "soft natural light", "dramatic chiaroscuro", "subtle rim light", "blue-hour light"],
    camera: ["85mm telephoto lens", "shallow depth of field", "wide-angle composition", "macro close-up", "low-angle view"],
    detail: ["intricate detail", "delicate textures", "photoreal materials", "rich tonal gradation", "crisp outlines"],
    mood: ["dreamlike atmosphere", "calm and still mood", "vivid colors", "muted tones", "surreal sense of air"],
    quality: ["ultra-high-res 8K", "professional photography", "ArtStation trending", "high-quality render", "refined finish"],
    join: ", ", end: ".",
  },
};

/* idle suggestion prompts (focus studio) */
const SUGGEST = {
  ko: ["비 내리는 네온 도시의 밤거리", "수채화 질감의 봄 꽃다발", "유리로 만든 초현실 액체 조각", "복고풍 신스웨이브 일몰", "심해를 유영하는 발광 해파리"],
  en: ["Neon city street at night in the rain", "Watercolor spring bouquet", "Surreal liquid sculpture of glass", "Retro synthwave sunset", "Glowing jellyfish in the deep sea"],
};

/* model one-line descriptions, indexed like MODELS */
const MODEL_DESC = {
  ko: ["최고 품질 · 표준", "사실적 묘사", "빠른 속도", "예술적 표현"],
  en: ["Top quality · standard", "Photorealistic", "Fast", "Artistic"],
};

/* gate feature chips */
const GATE_FEATURES = {
  ko: [["zap", "매일 무료 크레딧"], ["layers", "4종 AI 모델"], ["download", "고해상도 저장"]],
  en: [["zap", "Free daily credits"], ["layers", "4 AI models"], ["download", "Hi-res export"]],
};

Object.assign(window, {
  LangContext, UI, tr, useT, TAG_EN, tagLabel, PROMPT_DESC, SUGGEST, MODEL_DESC, GATE_FEATURES,
});
